import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Icon from './AppIcon';
import Modal from './ui/Modal';
import { mensajeError } from '../lib/errores';
import Campo from 'components/ui/Campo';

const PRIMARY = '#0f7a3d';
const FOREGROUND = '#404040';
const BORDER = '#E7E5E4';
const FORM_ID = 'change-password-modal-form';

export default function ChangePasswordModal({ isOpen, onClose, onSuccess }) {
  const { user } = useAuth();
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
      setError('');
      setShowPwd(false);
      setSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    if (!currentPwd) return 'Ingrese su contraseña actual.';
    if (newPwd.length < 6) return 'La contraseña nueva debe tener al menos 6 caracteres.';
    if (newPwd !== confirmPwd) return 'La contraseña nueva y la confirmación no coinciden.';
    if (newPwd === currentPwd) return 'La contraseña nueva debe ser distinta a la actual.';
    return null;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    if (!user?.email) { setError('Sesión inválida. Cierre sesión e intente de nuevo.'); return; }

    setLoading(true);
    setError('');

    // Verify current password by re-signing-in (no current_password param in Supabase API)
    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPwd,
    });
    if (signInErr) {
      setLoading(false);
      setError('Contraseña actual incorrecta.');
      return;
    }

    // Update to new password
    const { error: updErr } = await supabase.auth.updateUser({ password: newPwd });
    setLoading(false);
    if (updErr) {
      // Supabase responde en inglés; el caso común (la misma clave) va con
      // su texto, el resto por el traductor de shared.
      setError(/different from the old|same_password/i.test(`${updErr?.code || ''} ${updErr?.message || ''}`)
        ? 'La contraseña nueva tiene que ser distinta de la actual.'
        : mensajeError(updErr, 'No se pudo actualizar la contraseña.'));
      return;
    }

    setSuccess(true);
    onSuccess?.();
    setTimeout(() => onClose?.(), 1800);
  };

  const footer = !success ? (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={loading}
        className="flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-all disabled:opacity-50"
        style={{ borderColor: BORDER, color: FOREGROUND, backgroundColor: 'white' }}
      >
        Cancelar
      </button>
      <button
        type="submit"
        form={FORM_ID}
        disabled={loading}
        className="flex-1 px-4 py-3 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-60 active:scale-[0.98]"
        style={{ backgroundColor: loading ? '#b5d97a' : PRIMARY, minHeight: 48 }}
      >
        {loading ? 'Guardando...' : 'Cambiar contraseña'}
      </button>
    </>
  ) : null;

  return (
    <Modal open={isOpen} onClose={onClose} title="Cambiar contraseña" footer={footer} maxWidth="max-w-[420px]" ariaLabel="Cambiar contraseña">
      {success ? (
        <div className="px-0 py-4 flex flex-col items-center text-center gap-3">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: `${PRIMARY}20` }}>
            <Icon name="CheckCircle" size={28} color={PRIMARY} />
          </div>
          <p className="text-base font-semibold" style={{ color: FOREGROUND }}>Contraseña actualizada</p>
          <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>La próxima vez que entre, use la nueva.</p>
        </div>
      ) : (
      <form id={FORM_ID} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
          Cuenta: <span className="font-medium" style={{ color: FOREGROUND }}>{user?.email}</span>
        </p>

        <Campo label="Contraseña actual">
          <input
            type={showPwd ? 'text' : 'password'}
            value={currentPwd}
            onChange={(e) => setCurrentPwd(e.target.value)}
            autoComplete="current-password"
            disabled={loading}
            data-autofocus
            className="w-full px-3 py-3 text-base rounded-lg border focus:outline-none focus:ring-2"
            style={{ borderColor: BORDER, color: FOREGROUND, '--tw-ring-color': PRIMARY }}
          />
        </Campo>

        <Campo label="Nueva contraseña" ayuda="Mínimo 6 caracteres.">
          <input
            type={showPwd ? 'text' : 'password'}
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            autoComplete="new-password"
            disabled={loading}
            minLength={6}
            className="w-full px-3 py-3 text-base rounded-lg border focus:outline-none focus:ring-2"
            style={{ borderColor: BORDER, color: FOREGROUND }}
          />
        </Campo>

        <Campo label="Confirmar nueva contraseña">
          <input
            type={showPwd ? 'text' : 'password'}
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            autoComplete="new-password"
            disabled={loading}
            minLength={6}
            className="w-full px-3 py-3 text-base rounded-lg border focus:outline-none focus:ring-2"
            style={{ borderColor: BORDER, color: FOREGROUND }}
          />
        </Campo>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none" style={{ color: 'var(--color-muted-foreground)' }}>
          <input
            type="checkbox"
            checked={showPwd}
            onChange={(e) => setShowPwd(e.target.checked)}
            className="w-4 h-4 accent-current"
          />
          Mostrar contraseñas
        </label>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg border" style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)' }}>
            <Icon name="AlertCircle" size={14} color="var(--color-error)" />
            <p className="text-sm" style={{ color: 'var(--color-foreground)' }}>{error}</p>
          </div>
        )}
      </form>
      )}
    </Modal>
  );
}
